import TaskOutlinedIcon from '@mui/icons-material/TaskOutlined'
import { MapMarker } from '@app/components/Atoms/MapMarker'
import { Tooltip } from '@app/components/Atoms/Tooltip/Tooltip'
import bbox from '@turf/bbox'
import center from '@turf/center'
import { Feature, Geometry } from 'geojson'
import mapboxgl, { MapboxGeoJSONFeature, MapMouseEvent } from 'mapbox-gl'
import { createRoot } from 'react-dom/client'

export function addSelectedMarkerToMap({
  map,
  coordinates,
  colour,
}: {
  map: mapboxgl.Map
  coordinates: mapboxgl.LngLatLike
  colour: string
}) {
  removeSelectedMarkerFromMapIfExists()

  const selectedMarkerRef = document.createElement('div')
  selectedMarkerRef.id = 'selected-marker'

  createRoot(selectedMarkerRef).render(
    <MapMarker colour={colour}>
      <Tooltip title="Explore additional details within the information panel">
        <TaskOutlinedIcon />
      </Tooltip>
    </MapMarker>,
  )

  new mapboxgl.Marker(selectedMarkerRef).setLngLat(coordinates).addTo(map)
}

export function removeSelectedMarkerFromMapIfExists() {
  const selectedMarker = document.getElementById('selected-marker')
  if (selectedMarker) selectedMarker.remove()
}

type Coordinate = [number, number]
type BoundingCoordinates = { max_coord: Coordinate; min_coord: Coordinate }

function toGeoJSONFeature(
  feature: Feature<Geometry> | mapboxgl.MapboxGeoJSONFeature,
): Feature<Geometry> | null {
  if (!feature?.geometry) return null
  if ('type' in feature && feature.type === 'Feature') {
    // Fix geometry if it's not a valid GeoJSON
    let geometry = feature.geometry
    // If geometry is an object with type and coordinates, but coordinates is itself a geometry object, fix it
    if (
      geometry &&
      typeof geometry === 'object' &&
      'type' in geometry &&
      'coordinates' in geometry &&
      typeof geometry.coordinates === 'object' &&
      geometry.coordinates !== null &&
      'type' in geometry.coordinates &&
      'coordinates' in geometry.coordinates
    ) {
      // geometry.coordinates is actually a geometry object, so unwrap it
      geometry = geometry.coordinates as Geometry
    }
    return {
      ...feature,
      geometry,
    } as Feature<Geometry>
  }
  // Convert MapboxGeoJSONFeature to GeoJSON Feature
  return {
    type: 'Feature',
    geometry: feature.geometry,
    properties: feature.properties ?? {},
    id: feature.id,
  }
}

export function getBoundsOfFeature(
  feature: Feature<Geometry> | mapboxgl.MapboxGeoJSONFeature,
): BoundingCoordinates | null {
  const geojsonFeature = toGeoJSONFeature(feature)
  if (!geojsonFeature) return null
  const [minLng, minLat, maxLng, maxLat] = bbox(geojsonFeature)
  return {
    min_coord: [minLng, minLat],
    max_coord: [maxLng, maxLat],
  }
}

export function getCenterOfFeature(
  feature: Feature<Geometry> | mapboxgl.MapboxGeoJSONFeature,
): [number, number] | null {
  const geojsonFeature = toGeoJSONFeature(feature)
  if (!geojsonFeature) return null
  const centerFeature = center(geojsonFeature)
  return (centerFeature?.geometry?.coordinates as [number, number]) || null
}

export type GenericMapMouseEvents = {
  onClick?: (ev: MapMouseEvent & { features?: MapboxGeoJSONFeature[] | undefined }) => void
  onMouseEnter?: (ev: MapMouseEvent & { features?: MapboxGeoJSONFeature[] | undefined }) => void
  onMouseLeave?: (ev: MapMouseEvent & { features?: MapboxGeoJSONFeature[] | undefined }) => void
  onMouseMove?: (ev: MapMouseEvent & { features?: MapboxGeoJSONFeature[] | undefined }) => void
}

/**
 * Keeps track of the mouse events attached to map layers, automatically cleaning up old events when new events are added.
 */
export class MapMouseEventManager {
  private previousMouseEvents: GenericMapMouseEvents | null = null
  private previousLayerIds: string[] = []

  constructor(private map: mapboxgl.Map) {}

  public applyMouseEventsToLayers(layerIds: string[], mouseEvents: GenericMapMouseEvents) {
    this.removePreviousMouseEvents()
    this.addNewMouseEvents(layerIds, mouseEvents)
  }

  public removePreviousMouseEvents() {
    if (this.previousMouseEvents === null) return
    if (this.previousMouseEvents.onClick !== undefined) {
      for (const layerId of this.previousLayerIds) {
        this.map.off('click', layerId, this.previousMouseEvents.onClick)
      }
    }
    if (this.previousMouseEvents.onMouseEnter !== undefined) {
      for (const layerId of this.previousLayerIds) {
        this.map.off('mouseenter', layerId, this.previousMouseEvents.onMouseEnter)
      }
    }
    if (this.previousMouseEvents.onMouseLeave !== undefined) {
      for (const layerId of this.previousLayerIds) {
        this.map.off('mouseleave', layerId, this.previousMouseEvents.onMouseLeave)
      }
    }
    if (this.previousMouseEvents.onMouseMove !== undefined) {
      for (const layerId of this.previousLayerIds) {
        this.map.off('mousemove', layerId, this.previousMouseEvents.onMouseMove)
      }
    }
    this.previousMouseEvents = null
    this.previousLayerIds = []
  }

  private addNewMouseEvents(layerIds: string[], mouseEvents: GenericMapMouseEvents) {
    this.previousLayerIds = layerIds
    this.previousMouseEvents = mouseEvents

    if (mouseEvents.onClick !== undefined) {
      for (const layerId of layerIds) {
        this.map.on('click', layerId, mouseEvents.onClick)
      }
    }
    if (mouseEvents.onMouseEnter !== undefined) {
      for (const layerId of layerIds) {
        this.map.on('mouseenter', layerId, mouseEvents.onMouseEnter)
      }
    }
    if (mouseEvents.onMouseLeave !== undefined) {
      for (const layerId of layerIds) {
        this.map.on('mouseleave', layerId, mouseEvents.onMouseLeave)
      }
    }
    if (mouseEvents.onMouseMove !== undefined) {
      for (const layerId of layerIds) {
        this.map.on('mousemove', layerId, mouseEvents.onMouseMove)
      }
    }
  }
}
